import { CompatImage, createCanvas, createImage } from "../canvas/CanvasCompat";
import { serializeImageKey } from "../cache/CacheKey";
import { Caching } from "../cache/Caching";
import type { RequestResponse } from "../request/Requests";
import { Env } from "../Env";
import { Requests } from "../request/Requests";
import { WrappedImage } from "../WrappedImage";
import { ExtractableImageData } from "../ExtractableImageData";
import { Buffer } from "buffer";
import { prefix } from "../util/log";

const p = prefix("ImageLoader");

/** Encoded image bytes with dimensions and format detected from the file header. */
export interface ImageInfo {
    src?: string;
    width: number;
    height: number;
    type?: string;
    data: Buffer;
}

/** Fetches and decodes images through the active browser or Node environment provider. */
export class ImageLoader {

    protected static _createImage(): CompatImage {
        return createImage();
    }

    /** Resolves when an image URL has decoded, or rejects if decoding fails. */
    public static async loadAsync(src: string): Promise<CompatImage> {
        return new Promise<CompatImage>((resolve, reject) => {
            const image = this._createImage();
            image.onload = () => resolve(image);
            image.onerror = (err: Error) => reject(err instanceof Error ? err : new Error("Failed to decode image"));
            // Node images can finish decoding synchronously when src is assigned.
            image.src = src;
        });
    }

    /** Returns an image immediately. Use the callbacks to observe loading, or await {@link loadAsync}. */
    public static loadElement(src: string, onload?: () => void, onerr?: (err: Error) => void): CompatImage {
        const image = this._createImage();
        if (onload)
            image.onload = onload;
        if (onerr)
            image.onerror = onerr;
        image.src = src;
        return image;
    }

    public static async loadData(src: string): Promise<ImageData> {
        console.debug(p, "loadData", src);
        const image = await this.loadCanvasData(src);
        return image.data.getImageData(0, 0, image.width, image.height);
    }

    public static async loadCanvasData(src: string): Promise<ExtractableImageData> {
        const keyStr = serializeImageKey({ src });
        const info = this.getInfo(src);
        const cached = Caching.rawImageCache.getIfPresent(keyStr);
        try {
            return await this.infoToCanvasData(await info);
        } catch (err) {
            // A valid header can still contain corrupt pixels. Retry the fetch after a decode
            // failure, without evicting a replacement inserted while this load was pending.
            if (cached && Caching.rawImageCache.getIfPresent(keyStr) === cached) {
                Caching.rawImageCache.invalidate(keyStr);
            }
            throw err;
        }
    }

    /** Decodes the supplied bytes into a canvas without fetching the source URL again. */
    public static async infoToCanvasData(info: ImageInfo): Promise<ExtractableImageData> {
        const type = info.type === "jpg" ? "jpeg" : info.type ?? "png";
        // Both browser and Node images accept data URLs; src remains provenance, not a second fetch.
        const image = await ImageLoader.loadAsync(`data:image/${type};base64,${info.data.toString("base64")}`);
        const canvas = createCanvas(info.width, info.height);
        const context = canvas.getContext("2d") as CanvasRenderingContext2D;
        context.drawImage(image as CanvasImageSource, 0, 0);
        return {
            data: context,
            width: info.width,
            height: info.height
        }
    }


    public static async infoToData(info: ImageInfo): Promise<ImageData> {
        const image = await this.infoToCanvasData(info);
        return image.data.getImageData(0, 0, image.width, image.height);
    }

    /** Returns cached RGBA pixels for an image URL or data URL. Failed loads can be retried. */
    public static async getData(src: string): Promise<ImageData> {
        const keyStr = serializeImageKey({ src });
        return (await Caching.imageDataCache.get(keyStr, k => {
            return ImageLoader.loadData(src);
        }))!;
    }

    /** Returns a cached canvas context and dimensions for an image URL or data URL. */
    public static async getCanvasData(src: string): Promise<ExtractableImageData> {
        const keyStr = serializeImageKey({ src });
        return (await Caching.canvasImageDataCache.get(keyStr, k => {
            return ImageLoader.loadCanvasData(src);
        }))!;
    }

    public static async processResponse(response: Pick<RequestResponse<ArrayBuffer>, "data" | "url">): Promise<ImageInfo> {
        const src = response.url;
        const data = Buffer.from(response.data);
        const { width, height, type } = Env.provider.imageSize(data);
        if (!width || !height) {
            throw new Error("Invalid or unsupported image dimensions");
        }
        return {
            src,
            width,
            height,
            type,
            data: data
        }
    }


    public static async loadInfo(src: string): Promise<ImageInfo> {
        if (/^data:/i.test(src.trimStart())) {
            const url = new URL(src);
            url.hash = "";
            const comma = url.href.indexOf(",");
            if (comma < 0) {
                throw new Error("Invalid image data URL");
            }
            // Percent escapes represent image bytes, which need not be valid UTF-8.
            let bytes = url.href.slice(comma + 1).replace(/%([\da-f]{2})/gi,
                (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
            if (/; *base64 *$/i.test(url.href.slice(5, comma))) {
                bytes = atob(bytes);
            }
            return this.processResponse({
                url: src,
                data: Uint8Array.from(bytes, char => char.charCodeAt(0)).buffer
            });
        }

        return Requests.genericRequest({
            url: src,
            responseType: "arraybuffer"
        })
            .then(this.processResponse);
    }

    public static async getInfo(src: string): Promise<ImageInfo> {
        const keyStr = serializeImageKey({ src });
        return (await Caching.rawImageCache.get(keyStr, k => {
            return ImageLoader.loadInfo(src);
        }))!;
    }

    public static async loadWrapped(src: string): Promise<WrappedImage> {
        const data = await this.getCanvasData(src);
        return new WrappedImage(data);
    }

    public static async getWrapped(src: string): Promise<WrappedImage> {
        const keyStr = serializeImageKey({ src });
        return (await Caching.wrappedImageCache.get(keyStr, k => {
            return ImageLoader.loadWrapped(src);
        }))!;
    }


}
